import React, { useEffect, useState } from 'react';
import { useUpdateUserPreferencesMutation, useUserPreferences } from '@features/user-preferences';
import { Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

interface RecordingPreferencesFormProps {
  onSuccessfulFormSubmission?: () => void;
}

const RecordingPreferencesForm: React.FC<RecordingPreferencesFormProps> = ({
  onSuccessfulFormSubmission,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { recordingPreferences, isLoading: isLoadingPreferences } = useUserPreferences();
  const [updateUserPreferences, { isLoading: isSaving }] = useUpdateUserPreferencesMutation();

  // Local state for preferences
  const [stopMode, setStopMode] = useState<string>('stop_and_review');
  const [redactNames, setRedactNames] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState<{
    stopMode: string;
    redactNames: boolean;
  }>({ stopMode: 'stop_and_review', redactNames: false });

  // Initialize from user preferences
  useEffect(() => {
    const currentMode = recordingPreferences?.web_recording?.stop_mode || 'stop_and_review';
    const currentRedactNames = recordingPreferences?.redact_names ?? false;

    setStopMode(currentMode);
    setRedactNames(currentRedactNames);
    setOriginalValues({
      stopMode: currentMode,
      redactNames: currentRedactNames,
    });
    setHasChanges(false);
  }, [recordingPreferences]);

  // Handle radio change
  const handleStopModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setStopMode(newValue);
    setHasChanges(
      newValue !== originalValues.stopMode || redactNames !== originalValues.redactNames,
    );
  };

  // Handle checkbox change
  const handleRedactNamesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setRedactNames(newValue);
    setHasChanges(stopMode !== originalValues.stopMode || newValue !== originalValues.redactNames);
  };

  // Save preferences
  const handleSave = async () => {
    try {
      await updateUserPreferences({
        updateUserPreferencesRequest: {
          recording_preferences: {
            redact_names: redactNames,
            web_recording: {
              stop_mode: stopMode,
            },
          },
        },
      }).unwrap();

      setOriginalValues({
        stopMode,
        redactNames,
      });
      setHasChanges(false);
      onSuccessfulFormSubmission?.();
    } catch (error) {
      console.error('Failed to save recording preferences:', error);
      enqueueSnackbar('Failed to save recording preferences', { variant: 'error' });
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setStopMode(originalValues.stopMode);
    setRedactNames(originalValues.redactNames);
    setHasChanges(false);
  };

  if (isLoadingPreferences) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        {/* Stop Recording Behavior Section */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Recording Behavior
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure what happens when you stop a recording session.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 3 }}>
          {/* Stop Mode Setting */}
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2 }}>
              When I stop recording:
            </FormLabel>
            <RadioGroup value={stopMode} onChange={handleStopModeChange}>
              <FormControlLabel
                value="stop_and_review"
                control={<Radio disabled={isSaving} />}
                label={
                  <Box>
                    <Typography variant="body1">Stop and Review</Typography>
                    <Typography variant="body2" color="text.secondary">
                      After stopping, stay in the encounter to review the recording, add additional
                      dictations, or upload documents before creating the note.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: 'flex-start' }}
              />
              <FormControlLabel
                value="stop_and_create"
                control={<Radio disabled={isSaving} />}
                label={
                  <Box>
                    <Typography variant="body1">Stop and Generate Note</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically create the note immediately when the recording stops. Best for
                      simple encounters where you don't need to add additional information.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </RadioGroup>
          </FormControl>

          {/* Info box for auto-create mode */}
          {stopMode === 'stop_and_create' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Auto-create mode is enabled.</strong> When you click the stop button during
                a recording, the note will be automatically generated once all audio chunks have
                been uploaded. You can still add documents or dictations before stopping the
                recording.
              </Typography>
            </Alert>
          )}

          {/* Additional info about changing during recording */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> You can also change this setting directly from the recording
              widget during an encounter. This preference will be your default for all future
              recording sessions.
            </Typography>
          </Box>
        </Paper>

        {/* Privacy Section */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Privacy & Redaction
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Control how names appear in your generated clinical notes.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={redactNames}
                  onChange={handleRedactNamesChange}
                  disabled={isSaving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Redact names in generated notes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    When enabled, specific names mentioned during the encounter will be redacted and
                    replaced with generic terms in the final note content. This helps protect
                    privacy when names are spoken during recordings.
                  </Typography>
                </Box>
              }
            />
          </FormGroup>

          {/* Warning when redact names is enabled */}
          {redactNames && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Name redaction is enabled.</strong> Names mentioned in your recordings will
                be redacted in the generated note content. You can manually add names during note
                editing if needed.
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Unsaved changes alert */}
        {hasChanges && (
          <Alert severity="warning">You have unsaved changes to your recording preferences.</Alert>
        )}

        {/* Action buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          {hasChanges && (
            <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default RecordingPreferencesForm;
